import { Store } from 'react-notifications-component';
import { format, addMonths, parseISO, isAfter } from 'date-fns';

/*
 displayMessage("success | danger ", "String Title", `String Sub-title`);
*/
function displayMessage(type, title, message) {
  Store.addNotification({
    title: title,
    message: message,
    type: type,
    insert: "top",
    container: "bottom-left",
    animationIn: ["animate__animated", "animate__fadeIn"],
    animationOut: ["animate__animated", "animate__fadeOut"],
    dismiss: {
      duration: 5000,
      onScreen: false,
      pauseOnHover: true
    },

  });
}

function formatDateToDDMMMYYYY(date) {
  const months = ["Jan", "Feb", "Mar", "Apr", "May", "Jun",
    "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

  const day = String(date.getDate()).padStart(2, '0'); // Ensure 2 digits
  const month = months[date.getMonth()]; // Get the short month name
  const year = date.getFullYear(); // Get the full year

  return `${day}-${month}-${year}`;
}

/*
storageType : 'local' | 'session'
storageObjectName : string

*/
function setValue(storageType, storageObjectName, propertyName, value) {
  let storageObjectStr = null;
  if (storageType.toLowerCase() === 'local') {
    storageObjectStr = localStorage.getItem(storageObjectName);
  } else if (storageType.toLowerCase() === 'session') {
    storageObjectStr = sessionStorage.getItem(storageObjectName);
  }
  let storeObject = null;
  if (storageObjectStr !== null) {
    storeObject = JSON.parse(storageObjectStr);
    if (propertyName && storeObject) {
      storeObject[propertyName] = value;
    } else if (!propertyName && storeObject) {
      storeObject = value;
    } else {
      return false; // when propertyName is defined but not the storageObjectName
    }
  } else {
    storeObject = {};
    if (propertyName && storageObjectName) {
      storeObject[propertyName] = value;
    } else if (!propertyName && storeObject) {
      storeObject = value;
    } else {
      return false; // when propertyName is defined but not the storageObjectName
    }
  }

  if (storageType.toLowerCase() === 'local') {
    localStorage.setItem(storageObjectName, JSON.stringify(storeObject));
  } else if (storageType.toLowerCase() === 'session') {
    sessionStorage.setItem(storageObjectName, JSON.stringify(storeObject));
  }
  return true;
}

function getValue(storageType, storageObjectName, propertyName) {
  let storageObjectStr = null;
  if (storageType.toLowerCase() === 'local') {
    storageObjectStr = localStorage.getItem(storageObjectName);
  } else if (storageType.toLowerCase() === 'session') {
    storageObjectStr = sessionStorage.getItem(storageObjectName);
  }
  if (storageObjectStr !== null) {
    let storeObject = JSON.parse(storageObjectStr); //https://stackoverflow.com/questions/42494823/json-parse-returns-string-instead-of-object
    if (propertyName && storeObject.hasOwnProperty(propertyName)) {
      return storeObject[propertyName];
    } else {
      return storeObject;
    }
  }
  else {
    return null;
  }
}

function isEmpty(obj) {
  return Object.keys(obj).length === 0;
}



function generateMonthYearLabels(startDate, endDate) {
  if (!startDate || !endDate) return [];
  const start = parseISO(startDate);
  const end = parseISO(endDate);
  const labels = [];
  let current = start;

  while (!isAfter(current, end)) {
    labels.push(format(current, 'MMM yyyy')); // e.g., Jan 2025
    current = addMonths(current, 1);
  }

  return labels;
}

/**
 * Converts server ISO time to user's local time display (India format)
 * Input: "2025-09-08T05:56:37.492171" 
 * Output: "8/9/2025, 11:26 AM" (local IST)
 */
const convertServerTimeToLocal = (serverTime) => {
  if (!serverTime) return '';
  
  try {
    // Parse WITHOUT timezone first
    const utcDate = new Date(serverTime + '+00:00'); // Force UTC interpretation
    
    if (isNaN(utcDate.getTime())) return '';
    
    // Convert to user's LOCAL timezone - TIME ONLY
    return utcDate.toLocaleTimeString('en-IN', {
      timeZone: 'Asia/Kolkata',  // Explicit IST for India users
      hour: '2-digit',
      minute: '2-digit',
      hour12: true
    });
  } catch (error) {
    console.warn('Invalid server time:', serverTime);
    return '';
  }
};

const zeroIfNegative = (value) => Math.max(Number(value) || 0, 0);

/**
 * Turn a `frequency_year` label into a sortable integer, independent of the
 * forecast cadence. The backend (get_forecast_frequency_value) emits:
 *   Monthly    "03-2026"     -> MM-YYYY
 *   Weekly     "05-2026"     -> WW-YYYY   (week can be 1..53)
 *   Daily      "015-2026"    -> DDD-YYYY
 *   Quarterly  "Q1-2026"     -> Q#-YYYY   (Indian FY: Q1 = Apr)
 *   Yearly     "2026"        -> YYYY
 * Returns year*1000 + periodWithinYear so plain numeric compare orders correctly
 * across year boundaries (which naive string/`new Date(y, m-1)` compares do not).
 */
const periodSortKey = (label) => {
  const s = String(label ?? "").trim();
  const parts = s.split("-");
  if (parts.length === 1 && /^\d{4}$/.test(parts[0])) {
    return Number(parts[0]) * 1000;
  }
  if (parts.length === 2) {
    const [head, year] = parts;
    const y = Number(year);
    if (!Number.isFinite(y)) return 0;
    const q = head.match(/^Q(\d)$/i);
    if (q) return y * 1000 + Number(q[1]) * 100; // quarter
    if (/^\d+$/.test(head)) return y * 1000 + Number(head); // month / week / day-of-year
  }
  return 0;
};

/**
 * Human-facing column label for a `frequency_year` period.
 * For weekly / daily cadence the raw "WW-YYYY" / "DDD-YYYY" label is confusing
 * (e.g. "27-2026" reads like a date), so show the period's actual date instead.
 * Monthly / quarterly / yearly keep their raw label (already readable).
 */
const MONTHS_SHORT = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
const formatPeriodLabel = (period, isoDate, cadence) => {
  const c = String(cadence || "").toLowerCase();
  const dateBased = c === "weekly" || c === "daily";
  if (dateBased && isoDate) {
    const d = new Date(isoDate);
    if (!Number.isNaN(d.getTime())) {
      const dd = String(d.getDate()).padStart(2, "0");
      return `${dd} ${MONTHS_SHORT[d.getMonth()]} ${String(d.getFullYear()).slice(2)}`;
    }
  }
  return period;
};

/**
 * Chronological comparator for `frequency_year` labels. Prefers an explicit
 * ISO date string when available (most reliable), else falls back to
 * periodSortKey. Use: periods.sort((a, b) => comparePeriods(a, b)).
 */
const comparePeriods = (a, b, dateA, dateB) => {
  if (dateA && dateB) {
    const da = new Date(dateA).getTime();
    const db = new Date(dateB).getTime();
    if (Number.isFinite(da) && Number.isFinite(db) && da !== db) return da - db;
  }
  return periodSortKey(a) - periodSortKey(b);
};


export {
  displayMessage,
  formatDateToDDMMMYYYY,
  setValue,
  getValue,
  isEmpty,
  generateMonthYearLabels,
  convertServerTimeToLocal,
  zeroIfNegative,
  periodSortKey,
  comparePeriods,
  formatPeriodLabel
}


